import React, { useEffect, useState } from "react";
import { UserPlus } from "lucide-react";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { SwalWithTheme } from "../utils/sweetalert";
import { validarSenhaForte } from "../utils/validations";
import { formatElapsedSeconds } from "../utils/session";
import { criarUsuario, listarUsuariosInativosPaginado, listarUsuariosPaginado } from "../services/usuarioService";
import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarImage,
  Button,
  DataTable,
  FormField,
  Modal,
  SearchBar,
  StatusBadge,
  getAvatarInitials,
} from "../components/ui";

const PAGE_SIZE = 20;
const FILTER_STORAGE_KEY = "lista-usuarios:filtros:v2";

const userSchema = z.object({
  nome: z.string().min(1, { message: "Nome e obrigatorio" }),
  email: z.string().email({ message: "Email invalido" }),
  senha: z.string().min(6, { message: "Senha deve ter ao menos 6 caracteres" }),
  cargo: z.enum(["admin", "user"], {
    errorMap: () => ({ message: "Cargo e obrigatorio" }),
  }),
  supervisorIds: z.string().optional(),
});

function calcProgress(checks = []) {
  const total = checks.length || 1;
  const done = checks.filter(Boolean).length;
  return Math.round((done / total) * 100);
}

function loadFiltersFromSession() {
  const fallback = {
    page: 0,
    pageSize: PAGE_SIZE,
    mostrarInativos: false,
    searchTerm: "",
  };

  if (typeof window === "undefined") return fallback;

  try {
    const raw = window.sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return {
      page: Number.isFinite(parsed?.page) ? Math.max(0, Number(parsed.page)) : fallback.page,
      pageSize: Number.isFinite(parsed?.pageSize) ? Number(parsed.pageSize) : fallback.pageSize,
      mostrarInativos: Boolean(parsed?.mostrarInativos),
      searchTerm: String(parsed?.searchTerm || ""),
    };
  } catch {
    return fallback;
  }
}

export default function ListaUsuarios() {
  const navigate = useNavigate();
  const [initialFilters] = useState(() => loadFiltersFromSession());
  const [userPage, setUserPage] = useState({
    content: [],
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: PAGE_SIZE,
  });
  const [page, setPage] = useState(initialFilters.page);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [mostrarInativos, setMostrarInativos] = useState(initialFilters.mostrarInativos);
  const [searchTerm, setSearchTerm] = useState(initialFilters.searchTerm);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      nome: "",
      email: "",
      senha: "",
      cargo: "user",
      supervisorIds: "",
    },
  });

  const formNome = watch("nome");
  const formEmail = watch("email");
  const formSenha = watch("senha");
  const formCargo = watch("cargo");
  const formSupervisorIds = watch("supervisorIds");
  const usuarioModalProgress = calcProgress([
    Boolean(String(formNome || "").trim()),
    Boolean(String(formEmail || "").trim()),
    Boolean(String(formSenha || "").trim()),
    Boolean(String(formCargo || "").trim()),
    formCargo === "user" ? Boolean(String(formSupervisorIds || "").trim()) : true,
  ]);

  useEffect(() => {
    carregarUsuarios();
  }, [page, pageSize, mostrarInativos, searchTerm]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(
      FILTER_STORAGE_KEY,
      JSON.stringify({
        page,
        pageSize,
        mostrarInativos,
        searchTerm,
      })
    );
  }, [page, pageSize, mostrarInativos, searchTerm]);

  async function carregarUsuarios() {
    setLoading(true);
    try {
      const payload = { page, size: pageSize, q: searchTerm.trim() };
      const response = mostrarInativos
        ? await listarUsuariosInativosPaginado(payload)
        : await listarUsuariosPaginado({ ...payload, ativo: true });
      setUserPage(response);
    } catch {
      SwalWithTheme.error("Erro", "Erro ao carregar usuarios");
    } finally {
      setLoading(false);
    }
  }

  function abrirModalCriacao() {
    reset({
      nome: "",
      email: "",
      senha: "",
      cargo: "user",
      supervisorIds: "",
    });
    setShowCreateModal(true);
  }

  function fecharModalCriacao() {
    setShowCreateModal(false);
    reset({
      nome: "",
      email: "",
      senha: "",
      cargo: "user",
      supervisorIds: "",
    });
  }

  async function onSubmitUsuario(data) {
    const validacao = validarSenhaForte(data.senha || "");
    if (!validacao.valida) {
      SwalWithTheme.error("Senha invalida", validacao.mensagem);
      return;
    }

    try {
      const criado = await criarUsuario(data);
      fecharModalCriacao();
      await carregarUsuarios();
      SwalWithTheme.success("Sucesso", "Usuario criado");
      navigate(`/usuarios/${criado.id}`);
    } catch {
      SwalWithTheme.error("Erro", "Erro ao salvar usuario");
    }
  }

  const columns = [
    {
      key: "nome",
      header: "Usuario",
      mobilePrimary: true,
      mobileOrder: 0,
      mobileSpan: 2,
      mobileSlot: "title",
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <Avatar className="h-12 w-12 border-border bg-muted text-foreground">
            <AvatarImage src={row.fotoPerfilUrl} alt={row.nome || "Usuario"} />
            <AvatarFallback>{getAvatarInitials(row.nome)}</AvatarFallback>
            <AvatarBadge tone={row.sessaoAtiva ? "online" : "offline"} />
          </Avatar>
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{row.nome}</div>
            <div className="truncate text-xs text-muted-foreground">{row.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: "cargo",
      header: "Cargo",
      mobileSpan: 1,
      mobileOrder: 1,
      mobileSlot: "badge",
      render: (value) => (
        <StatusBadge status={value === "admin" ? "Administrador" : "Comercial"} variant="info" />
      ),
    },
    {
      key: "sessaoAtiva",
      header: "Conexao",
      mobileSpan: 1,
      mobileOrder: 2,
      mobileSlot: "badge",
      render: (_, row) => (
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge
            status={row.sessaoAtiva ? "Online" : "Offline"}
            variant={row.sessaoAtiva ? "success" : "danger"}
          />
          {!row.sessaoAtiva && row.tempoDesconectadoSegundos != null ? (
            <span className="text-xs text-muted-foreground">
              ha {formatElapsedSeconds(row.tempoDesconectadoSegundos)}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      key: "ativo",
      header: "Status",
      mobileSpan: 1,
      mobileOrder: 3,
      mobileSlot: "badge",
      render: (value) => (
        <StatusBadge status={value ? "Ativo" : "Inativo"} variant={value ? "success" : "danger"} />
      ),
    },
    {
      key: "dataAtualizacao",
      header: "Atualizado em",
      mobileSpan: 2,
      mobileOrder: 4,
      mobileSlot: "footer",
      mobileRender: (value) => (
        <span className="text-xs text-muted-foreground">
          {value
            ? `Atualizado em ${new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}`
            : "Sem atualização registrada"}
        </span>
      ),
      render: (value) => (value ? new Date(value).toLocaleString("pt-BR") : "N/A"),
    },
  ];

  return (
    <div className="space-y-4">


      <section className="rounded-xl border border-border bg-card p-3 shadow-sm sm:p-4">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setPage(0);
                setMostrarInativos((prev) => !prev);
              }}
            >
              {mostrarInativos ? "Mostrar ativos" : "Mostrar inativos"}
            </Button>
            <Button onClick={abrirModalCriacao}>
              <UserPlus className="h-4 w-4" />
              Novo usuario
            </Button>
          </div>
          <SearchBar
            placeholder="Pesquisar usuarios..."
            value={searchTerm}
            onChange={(event) => {
              setPage(0);
              setSearchTerm(event.target.value);
            }}
            className="w-full sm:max-w-md"
          />
        </div>

        <DataTable
          columns={columns}
          data={userPage.content}
          onRowClick={(usuario) => navigate(`/usuarios/${usuario.id}`)}
          loading={loading}
          pagination={userPage}
          onPageChange={(target) => setPage(target)}
          onPageSizeChange={(size) => {
            setPage(0);
            setPageSize(size);
            setUserPage((prev) => ({ ...prev, size }));
          }}
          enablePagination
        />
      </section>

      <Modal
        isOpen={showCreateModal}
        onClose={fecharModalCriacao}
        title="Novo usuario"
        onSave={handleSubmit(onSubmitUsuario)}
        onCancel={fecharModalCriacao}
        progress={usuarioModalProgress}
        progressLabel="Preenchimento do usuario"
      >
        <form onSubmit={handleSubmit(onSubmitUsuario)} className="space-y-3">
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <FormField
                label="Nome"
                type="text"
                placeholder="Nome completo"
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.nome?.message}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <FormField
                label="Email"
                type="email"
                placeholder="email@empresa.com"
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.email?.message}
              />
            )}
          />

          <Controller
            name="senha"
            control={control}
            render={({ field }) => (
              <FormField
                label="Senha"
                type="password"
                placeholder="Digite a senha"
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.senha?.message}
              />
            )}
          />

          <Controller
            name="supervisorIds"
            control={control}
            render={({ field }) => (
              <FormField
                label="Supervisor ID(s) Join"
                type="text"
                placeholder="Ex.: 3738, 21077"
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={errors.supervisorIds?.message}
              />
            )}
          />

          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">Cargo</label>
            <select
              {...register("cargo")}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="admin">Administrador</option>
              <option value="user">Comercial</option>
            </select>
            {errors.cargo?.message && (
              <p className="mt-1 text-xs text-destructive">{errors.cargo.message}</p>
            )}
          </div>
        </form>
      </Modal>
    </div>
  );
}
